import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../../../components/layout/DashboardLayout';
import FilterBar from './components/FilterBar';
import KPICard from './components/KPICard';
import ReportChart from './components/ReportChart';
import ReportAnalysis from './components/ReportAnalysis';
import ExportButton from './components/ExportButton';
import { getGeneralReports } from '../../../services/reportService';
import { CHART_COLORS } from './utils/chartConfig';
import { prepareKPIsForExport } from './utils/exportUtils';
import { useI18n } from '../../../i18n/I18nContext';
import '../../../styles/tables.css';
import './Reports.css';

/**
 * GeneralReports Component
 * Displays general performance metrics and analytics
 */
const GeneralReports = () => {
    const { t } = useI18n();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({});
    const [reportData, setReportData] = useState(null);

    // Fetch report data
    const fetchReportData = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await getGeneralReports(filters);
            setReportData(response.data);
        } catch (err) {
            console.error('Error fetching general reports:', err);
            setError('Erreur lors du chargement des données. Veuillez réessayer.');
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        fetchReportData();
    }, [fetchReportData]);

    // Handle filter changes
    const handleFilterChange = (newFilters) => {
        setFilters(newFilters);
    };

    // Prepare chart data
    const revenueByPeriodData = reportData?.revenueByPeriod ? {
        labels: reportData.revenueByPeriod.map(item => item.period),
        datasets: [
            {
                label: 'Chiffre d\'affaires',
                data: reportData.revenueByPeriod.map(item => item.revenue),
                backgroundColor: CHART_COLORS.primary,
                borderRadius: 8,
                maxBarThickness: 60
            }
        ]
    } : null;

    const statusDistributionData = reportData?.statusDistribution ? {
        labels: reportData.statusDistribution.map(item => item.status),
        datasets: [
            {
                data: reportData.statusDistribution.map(item => item.count),
                backgroundColor: [
                    CHART_COLORS.completed,
                    CHART_COLORS.active,
                    CHART_COLORS.pending,
                    CHART_COLORS.cancelled
                ],
                borderWidth: 0
            }
        ]
    } : null;

    const revenueByClientData = reportData?.revenueByClient ? {
        labels: reportData.revenueByClient.map(item => item.clientName),
        datasets: [
            {
                label: 'Chiffre d\'affaires',
                data: reportData.revenueByClient.map(item => item.revenue),
                backgroundColor: CHART_COLORS.secondary,
                borderRadius: 8,
                maxBarThickness: 60
            }
        ]
    } : null;

    const revenueByProductData = reportData?.revenueByProduct ? {
        labels: reportData.revenueByProduct.map(item => item.productName),
        datasets: [
            {
                data: reportData.revenueByProduct.map(item => item.revenue),
                backgroundColor: CHART_COLORS.series,
                borderWidth: 0
            }
        ]
    } : null;

    const exportData = reportData ? [
        ...prepareKPIsForExport(reportData.kpis),
        { Indicateur: '', Valeur: '' },
        { Indicateur: 'CA par Période', Valeur: '' },
        ...(reportData.revenueByPeriod || []).map(item => ({ Indicateur: item.period, Valeur: item.revenue })),
        { Indicateur: '', Valeur: '' },
        { Indicateur: 'Répartition par Statut', Valeur: '' },
        ...(reportData.statusDistribution || []).map(item => ({ Indicateur: item.status, Valeur: item.count })),
        { Indicateur: '', Valeur: '' },
        { Indicateur: 'Top 10 Clients', Valeur: '' },
        ...(reportData.revenueByClient || []).map(item => ({ Indicateur: item.clientName, Valeur: item.revenue })),
        { Indicateur: '', Valeur: '' },
        { Indicateur: 'Par Produit', Valeur: '' },
        ...(reportData.revenueByProduct || []).map(item => ({ Indicateur: item.productName, Valeur: item.revenue }))
    ] : null;

    return (
        <DashboardLayout>
            <div className="reports-page">
                {/* Header */}
                <div className="reports-header">
                    <div>
                        <h1 className="reports-title">Rapports & Analytics</h1>
                        <p className="reports-subtitle">Rapports généraux</p>
                    </div>
                    <ExportButton
                        reportName="rapports_generaux"
                        data={exportData}
                        headers={['Indicateur', 'Valeur']}
                        keys={['Indicateur', 'Valeur']}
                        disabled={loading || !reportData}
                    />
                </div>

                {/* Filters */}
                <FilterBar
                    onFilterChange={handleFilterChange}
                    availableFilters={['currency', 'period', 'status', 'client']}
                />

                {/* Error State */}
                {error && (
                    <div className="alert alert-danger rounded-3 border-0 shadow-sm">
                        <i className="bi bi-exclamation-triangle me-2"></i>
                        {error}
                    </div>
                )}

                {/* Analysis Section */}
                {reportData?.analysis && (
                    <ReportAnalysis analysis={reportData.analysis} />
                )}

                {/* KPI Cards */}
                <div className="row g-4 mb-4">
                    <div className="col-xl-3 col-md-6">
                        <KPICard
                            title="Chiffre d'affaires total"
                            value={reportData?.kpis?.totalRevenue}
                            previousValue={reportData?.kpis?.previousTotalRevenue}
                            format="currency"
                            currency={filters.currency || 'EUR'}
                            icon="bi bi-currency-euro"
                            gradient="blue"
                            loading={loading}
                        />
                    </div>
                    <div className="col-xl-3 col-md-6">
                        <KPICard
                            title="Nombre de transports"
                            value={reportData?.kpis?.transportCount}
                            previousValue={reportData?.kpis?.previousTransportCount}
                            format="number"
                            icon="bi bi-truck"
                            gradient="green"
                            loading={loading}
                        />
                    </div>
                    <div className="col-xl-3 col-md-6">
                        <KPICard
                            title="CA moyen/transport"
                            value={reportData?.kpis?.avgRevenuePerTransport}
                            previousValue={reportData?.kpis?.previousAvgRevenuePerTransport}
                            format="currency"
                            currency={filters.currency || 'EUR'}
                            icon="bi bi-graph-up"
                            gradient="purple"
                            loading={loading}
                        />
                    </div>
                    <div className="col-xl-3 col-md-6">
                        <KPICard
                            title="Taux de croissance"
                            value={reportData?.kpis?.growthRate}
                            format="percentage"
                            icon="bi bi-arrow-up-right"
                            gradient="orange"
                            loading={loading}
                            decimals={1}
                        />
                    </div>
                </div>

                {/* Charts */}
                <div className="row g-4 mb-4">
                    <div className="col-lg-8">
                        <ReportChart
                            type="bar"
                            data={revenueByPeriodData}
                            title="Chiffre d'affaires par période"
                            height={350}
                            loading={loading}
                            id="revenue-chart"
                            options={{ dataType: 'currency' }}
                        />
                    </div>
                    <div className="col-lg-4">
                        <ReportChart
                            type="doughnut"
                            data={statusDistributionData}
                            title="Répartition par statut"
                            height={350}
                            loading={loading}
                        />
                    </div>
                </div>

                <div className="row g-4">
                    <div className="col-lg-6">
                        <ReportChart
                            type="bar"
                            data={revenueByClientData}
                            title="Chiffre d'affaires par client (Top 10)"
                            height={350}
                            loading={loading}
                            options={{ dataType: 'currency' }}
                        />
                    </div>
                    <div className="col-lg-6">
                        <ReportChart
                            type="pie"
                            data={revenueByProductData}
                            title="Chiffre d'affaires par produit de facturation"
                            height={350}
                            loading={loading}
                            options={{ dataType: 'currency' }}
                        />
                    </div>
                </div>
            </div>
        </DashboardLayout>
    );
};

export default GeneralReports;
